import { Box } from '@mui/material';
import { useDeleteResourceById } from '_api/mutations';
import { Resource } from '_api/types';
import { ResourceRouteMap } from '_constants';
import { ThumbnailView } from 'components/atoms';
import { ActionsFlexBox } from 'components/molecules';
import { ViewButtons } from 'components/molecules/types';
import { TableHeader } from 'components/organisms';
import { AddResourceToPatientModal } from 'components/organisms/modals';
import { MaterialReactTable, MRT_ColumnDef } from 'material-react-table';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigateTo } from 'utils';
import { useResources, useSearchResults, useUser } from 'utils/hooks';

export const ResourceManagement = () => {
  const { existingSearchTerm, updateSearchTerm } = useSearchResults();
  const navigate = useNavigateTo();
  const { canEditObject } = useUser();
  const toResourceFormView = () => navigate(ResourceRouteMap.ADDFORM);
  const { allResources, isLoading } = useResources();
  const { mutateAsync: deleteResource } = useDeleteResourceById();
  const [id, setId] = useState(0);
  const [resource, setResource] = useState<Resource | undefined>(undefined);
  const [searchTerm, setSearchTerm] = useState(existingSearchTerm || '');
  const [filteredData, setFilteredData] = useState<Resource[]>([]);

  useEffect(() => setSearchTerm(existingSearchTerm || ''), [existingSearchTerm]);

  useEffect(() => {
    if (allResources) {
      setFilteredData(allResources);
    }
  }, [allResources]);

  useMemo(() => {
    if (allResources) {
      const lowercasedSearchTerm = searchTerm.toLowerCase();
      const filtered = allResources.filter((resource) => {
        const title = resource.title.toLowerCase();
        return title.includes(lowercasedSearchTerm);
      });
      setFilteredData(filtered);
    }
  }, [searchTerm, allResources]);

  const handleViewOnClick = (id: number) => {
    navigate(ResourceRouteMap.DETAIL, id.toString());
  };

  const handleEditOnClick = (id: number) => {
    navigate(ResourceRouteMap.EDIT, id.toString());
  };

  const handleDeleteOnClick = (id: number) => {
    deleteResource({ id });
  };

  const handleAddOnClick = (id: number, res: Resource) => {
    setId(id);
    setResource(res);
  };

  const handleSearch = (searchTerm: string) => {
    setSearchTerm(searchTerm);
    updateSearchTerm(searchTerm);
  };

  const actionButtons: ViewButtons = {
    viewButton: true,
    editButton: true,
    deleteButton: true,
    addButton: true,
  };

  const tableColumns = useMemo<MRT_ColumnDef<Resource>[]>(
    () => [
      {
        header: 'Resources',
        enableSorting: false,
        Cell: ({ row }) => (
          <ThumbnailView fullImageUrl={row.original.fullImageUrl} rowView={true} />
        ),
      },
      {
        header: 'Title',
        accessorFn: (row) => row.title,
      },
      {
        header: 'Actions',
        enableSorting: false,
        accessorFn: (row) => (
          <ActionsFlexBox
            msg={'resource'}
            viewOnClick={() => handleViewOnClick(row.id)}
            editOnClick={() => handleEditOnClick(row.id)}
            deleteOnClick={() => handleDeleteOnClick(row.id)}
            addOnClick={() => handleAddOnClick(row.id, row)}
            actionButtons={actionButtons}
            disableDeleteTooltip={
              canEditObject(row)
                ? undefined
                : 'Cannot delete resources you did not create'
            }
            disableEditTooltip={
              canEditObject(row) ? undefined : 'Cannot edit resources you did not create'
            }
          />
        ),
        size: 10,
      },
    ],
    [],
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <TableHeader
          onAddClick={toResourceFormView}
          handleSearch={handleSearch}
          value={searchTerm}
        />
      </Box>
      <Box sx={{ marginTop: 10 }}>
        {filteredData && !isLoading ? (
          <MaterialReactTable
            columns={tableColumns}
            data={filteredData}
            enableSorting={true}
            enableColumnActions={false}
            enableTopToolbar={false}
            enablePagination={true}
          />
        ) : null}
      </Box>
      <AddResourceToPatientModal
        content={resource as Resource}
        open={!!resource}
        onClose={() => setResource(undefined)}
        id={id}
      />
    </Box>
  );
};
